import classNames from 'classnames'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import { useMouseHover } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { Skeleton } from '../../Skeleton'
import {
  QA_NAME_TAG,
  TAG_APPEARANCE,
  TAG_HEIGHT,
  TAG_HEIGHT_CLASSNAME,
} from '../Tag.constants'
import { useTagInteractiveHandlers } from '../Tag.hooks'
import { getTagStyle, isTagAppearanceFilled } from '../Tag.utils'
import { TagActions } from '../TagActions'
import { TagContent } from '../TagContent'

import * as styles from './Tag.module.css'

function Tag({
  afterContent,
  appearance = TAG_APPEARANCE.outlined,
  backgroundColor,
  backgroundColorHover,
  beforeContent,
  className = styles.Tag,
  height = TAG_HEIGHT.height32,
  isDisabled,
  isError,
  isLoading,
  isSkeleton,
  meta,
  qaContext = QA_NAME_TAG,
  qaName,
  removeIconColor,
  removeTooltipKey,
  secondText,
  text,
  textClassName,
  textColor,
  textColorHover,
  textQaName,
  tooltip,
  tooltipPlacement = FLOATING_ELEMENT_PLACEMENT.top,
  withTooltip = true,
  onClick,
  onMouseDown,
  onRemove,
}) {
  const isFilled = isTagAppearanceFilled(appearance)
  const isInteractive = Boolean(onClick) && !isDisabled && !isLoading
  const showProgress = !isFilled && isLoading
  const showRemove =
    Boolean(onRemove) && !isDisabled && !(isFilled && isLoading)
  const hasActions = Boolean(afterContent) || showProgress || showRemove

  const [removeHoverRef, isRemoveHovered] = useMouseHover()

  const { handleClick, handleKeyDown } = useTagInteractiveHandlers({
    isDisabled,
    onClick,
  })

  const qaProps = getQaSelector({ context: qaContext, value: qaName })

  const tagStyle = getTagStyle({
    backgroundColor,
    backgroundColorHover,
    removeIconColor,
    textColor,
    textColorHover,
  })

  const heightClassName = styles[TAG_HEIGHT_CLASSNAME[height]]

  const tagClassName = classNames(
    styles[appearance],
    heightClassName,
    {
      [styles.clickable]: isInteractive,
      [styles.disabled]: isDisabled,
      [styles.error]: isError && isFilled,
      [styles.loading]: isLoading,
      [styles.removeHovered]: isFilled && isRemoveHovered,
    },
    className
  )

  return isSkeleton ? (
    <Skeleton
      {...qaProps}
      className={classNames(heightClassName, styles.skeleton, className)}
    />
  ) : (
    <div
      {...qaProps}
      aria-disabled={isDisabled || undefined}
      className={tagClassName}
      role={isInteractive ? 'button' : undefined}
      style={tagStyle}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={isInteractive ? handleKeyDown : undefined}
      onMouseDown={onMouseDown}
    >
      {beforeContent}

      <TagContent
        isFilled={isFilled}
        secondText={secondText}
        text={text}
        textClassName={textClassName}
        textQaName={textQaName}
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        withMeta={Boolean(meta)}
        withTooltip={withTooltip}
      />

      {meta ? <div className={styles.metaWrapper}>{meta}</div> : null}

      {hasActions ? (
        <TagActions
          afterContent={afterContent}
          appearance={appearance}
          height={height}
          isDisabled={isDisabled}
          isFilled={isFilled}
          qaName={qaName}
          removeHoverRef={removeHoverRef}
          removeTooltipKey={removeTooltipKey}
          showProgress={showProgress}
          showRemove={showRemove}
          tooltipPlacement={tooltipPlacement}
          onRemove={onRemove}
        />
      ) : null}
    </div>
  )
}

export default Tag
